import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { catchError, forkJoin, of } from 'rxjs';
import { jsPDF } from 'jspdf';
import { AdressesService, Adresse } from '../../../services/adresses.service';
import { CommandesService, LignesPreparationItem } from '../../../services/commandes.service';

@Component({
  selector: 'app-lignes-preparation-list',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './lignes-preparation-list.component.html',
  styleUrls: ['./lignes-preparation-list.component.scss']
})
export class LignesPreparationListComponent implements OnInit {
  lignes: LignesPreparationItem[] = [];
  search = '';
  pageIndex = 0;
  pageSize = 20;
  loading = false;
  error = '';
  selectedIds: Set<number> = new Set();
  private readonly commandeNumeroById = new Map<number, string>();
  private readonly adresseById = new Map<number, Adresse>();

  constructor(
    private readonly commandesService: CommandesService,
    private readonly adressesService: AdressesService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadLignes();
  }

  get filteredLignes(): LignesPreparationItem[] {
    const needle = this.normalize(this.search);
    if (!needle) {
      return this.lignes;
    }

    return this.lignes.filter((ligne) => {
      const haystack = [
        ligne.id,
        this.getCommandeNumero(ligne),
        this.getAdresseLivraisonLabel(ligne),
        ligne.type_capteur,
        ligne.cartouche_a,
        ligne.nom_cartouche_a,
        ligne.cartouche_b,
        ligne.nom_cartouche_b,
        ligne.date_expedition,
        ligne.statut,
        ligne.statut_nom,
      ]
        .map((v) => this.normalize(v))
        .join(' ');

      return haystack.includes(needle);
    });
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  get allPagedSelected(): boolean {
    if (!this.pagedLignes.length) {
      return false;
    }
    return this.pagedLignes.every((ligne) => this.selectedIds.has(ligne.id));
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredLignes.length / this.pageSize));
  }

  get pagedLignes(): LignesPreparationItem[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredLignes.slice(start, start + this.pageSize);
  }

  onSearch(): void {
    this.pageIndex = 0;
  }

  prevPage(): void {
    this.pageIndex = Math.max(0, this.pageIndex - 1);
  }

  nextPage(): void {
    this.pageIndex = Math.min(this.totalPages - 1, this.pageIndex + 1);
  }

  formatDateFr(value: string | null | undefined): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '-';
    }

    const yyyyMmDdMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (yyyyMmDdMatch) {
      const [, y, m, d] = yyyyMmDdMatch;
      return `${d}/${m}/${y}`;
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('fr-FR');
    }

    return raw;
  }

  getCommandeNumero(ligne: LignesPreparationItem): string {
    const direct = String(ligne.numero_commande ?? '').trim();
    if (direct) {
      return direct;
    }

    const fromCache = this.commandeNumeroById.get(ligne.commande_id);
    if (fromCache) {
      return fromCache;
    }

    return `Commande #${ligne.commande_id}`;
  }

  getAdresseLivraisonLabel(ligne: LignesPreparationItem): string {
    const adresseId = ligne.adresse_livraison_id;
    if (!adresseId) {
      return '-';
    }

    const adresse = this.adresseById.get(adresseId);
    if (!adresse) {
      return `Adresse #${adresseId}`;
    }

    const morceaux = [
      String(adresse.adresse ?? '').trim(),
      String(adresse.code_postal ?? '').trim(),
      String(adresse.ville ?? '').trim(),
      String(adresse.pays ?? '').trim(),
    ].filter(Boolean);

    return morceaux.length ? morceaux.join(', ') : `Adresse #${adresseId}`;
  }

  isSelected(ligneId: number): boolean {
    return this.selectedIds.has(ligneId);
  }

  onToggleLigne(ligneId: number, checked: boolean): void {
    if (checked) {
      this.selectedIds.add(ligneId);
      return;
    }
    this.selectedIds.delete(ligneId);
  }

  onTogglePage(checked: boolean): void {
    for (const ligne of this.pagedLignes) {
      if (checked) {
        this.selectedIds.add(ligne.id);
      } else {
        this.selectedIds.delete(ligne.id);
      }
    }
  }

  exportSelectedToPdf(): void {
    const selection = this.lignes.filter((ligne) => this.selectedIds.has(ligne.id));
    if (!selection.length) {
      this.error = 'Sélectionnez au moins une ligne pour exporter le PDF.';
      return;
    }

    this.error = '';

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const marginX = 28;
    const marginY = 32;
    const rowPadding = 4;
    const rowLineHeight = 11;
    const headerHeight = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usableWidth = pageWidth - marginX * 2;
    let y = marginY;

    doc.setFontSize(14);
    doc.text('Export PDF - Lignes de commandes sélectionnées', marginX, y);
    y += 20;

    doc.setFontSize(10);
    doc.text(`Généré le ${new Date().toLocaleString('fr-FR')}`, marginX, y);
    y += 18;

    const columns: Array<{ key: string; title: string; widthRatio: number }> = [
      { key: 'ligne', title: 'Ligne', widthRatio: 0.08 },
      { key: 'commande', title: 'Commande', widthRatio: 0.14 },
      { key: 'adresse', title: 'Adresse livraison', widthRatio: 0.27 },
      { key: 'type', title: 'Type capteur', widthRatio: 0.12 },
      { key: 'cartouche_a', title: 'Cartouche A', widthRatio: 0.13 },
      { key: 'cartouche_b', title: 'Cartouche B', widthRatio: 0.13 },
      { key: 'date', title: "Date d'expédition souhaitée", widthRatio: 0.13 },
    ];

    const columnWidths = columns.map((column) => Math.floor(column.widthRatio * usableWidth));
    const widthDelta = usableWidth - columnWidths.reduce((sum, value) => sum + value, 0);
    columnWidths[columnWidths.length - 1] += widthDelta;

    const drawTableHeader = (): void => {
      doc.setDrawColor(190);
      doc.setFillColor(240, 240, 240);
      doc.setLineWidth(0.5);
      doc.rect(marginX, y, usableWidth, headerHeight, 'FD');

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      let x = marginX;
      columns.forEach((column, index) => {
        if (index > 0) {
          doc.line(x, y, x, y + headerHeight);
        }
        doc.text(column.title, x + rowPadding, y + 13);
        x += columnWidths[index];
      });
      doc.setFont('helvetica', 'normal');
      y += headerHeight;
    };

    drawTableHeader();

    selection.forEach((ligne) => {
      const rowValues: Record<string, string> = {
        ligne: String(ligne.id),
        commande: this.getCommandeNumero(ligne),
        adresse: this.getAdresseLivraisonLabel(ligne),
        type: String(ligne.type_capteur || '-'),
        cartouche_a: String(ligne.nom_cartouche_a || ligne.cartouche_a || '-'),
        cartouche_b: String(ligne.nom_cartouche_b || ligne.cartouche_b || '-'),
        date: this.formatDateFr(ligne.date_expedition),
      };

      const cellLines = columns.map((column, index) => {
        const text = rowValues[column.key] || '-';
        const textWidth = Math.max(12, columnWidths[index] - rowPadding * 2);
        const wrapped = doc.splitTextToSize(text, textWidth);
        return wrapped.length ? wrapped : ['-'];
      });

      const maxLines = Math.max(...cellLines.map((lines) => lines.length));
      const rowHeight = Math.max(18, maxLines * rowLineHeight + rowPadding * 2);

      if (y + rowHeight > pageHeight - marginY) {
        doc.addPage();
        y = marginY;
        drawTableHeader();
      }

      doc.setDrawColor(210);
      doc.rect(marginX, y, usableWidth, rowHeight);

      let x = marginX;
      columns.forEach((_, index) => {
        if (index > 0) {
          doc.line(x, y, x, y + rowHeight);
        }
        doc.text(cellLines[index], x + rowPadding, y + rowPadding + 8);
        x += columnWidths[index];
      });

      y += rowHeight;
    });

    const datePart = new Date().toISOString().split('T')[0];
    doc.save(`lignes_commandes_selection_${datePart}.pdf`);
  }

  private loadLignes(): void {
    this.loading = true;
    this.error = '';

    this.commandesService.listLignesEnCoursDePreparation().subscribe({
      next: (response) => {
        this.lignes = response?.items ?? [];
        this.selectedIds.clear();
        this.hydrateDisplayData(this.lignes);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Impossible de charger les lignes de commande.';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private hydrateDisplayData(lignes: LignesPreparationItem[]): void {
    const commandeIds = Array.from(new Set(
      lignes
        .map((ligne) => Number(ligne.commande_id))
        .filter((id) => Number.isFinite(id) && id > 0)
    ));

    const adresseIds = Array.from(new Set(
      lignes
        .map((ligne) => Number(ligne.adresse_livraison_id))
        .filter((id) => Number.isFinite(id) && id > 0)
    ));

    if (!commandeIds.length && !adresseIds.length) {
      return;
    }

    const missingCommandeIds = commandeIds.filter((id) => !this.commandeNumeroById.has(id));
    const missingAdresseIds = adresseIds.filter((id) => !this.adresseById.has(id));

    const commandQueries = missingCommandeIds.map((id) =>
      this.commandesService.get(id).pipe(
        catchError(() => of(null))
      )
    );

    const adresseQueries = missingAdresseIds.map((id) =>
      this.adressesService.get(id).pipe(
        catchError(() => of(null))
      )
    );

    if (!commandQueries.length && !adresseQueries.length) {
      return;
    }

    forkJoin({
      commandes: commandQueries.length ? forkJoin(commandQueries) : of([]),
      adresses: adresseQueries.length ? forkJoin(adresseQueries) : of([]),
    }).subscribe(({ commandes, adresses }) => {
      commandes.forEach((commande, index) => {
        if (!commande) {
          return;
        }
        const commandeId = missingCommandeIds[index];
        const numero = String(commande.numero_commande ?? '').trim();
        if (numero) {
          this.commandeNumeroById.set(commandeId, numero);
        }
      });

      adresses.forEach((adresse, index) => {
        if (!adresse) {
          return;
        }
        const adresseId = missingAdresseIds[index];
        this.adresseById.set(adresseId, adresse);
      });

      this.cdr.detectChanges();
    });
  }

  private normalize(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}